import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { getReporteVentas } from '../../service/reportes/ReporteVentas';
import { getReporteVentasCortesias } from '../../service/reportes/VentasCortesias';
import { getAllReportConciliations } from '../../service/conciliation/Conciliation';
import { getReporteRetirosDashboard, getMontoLimiteRetiro, getVentasPorCaja } from '../../service/dashboard/Dashboard';
import { getReporteRemanente } from '../../service/reportes/ReporteRemanente';

export interface DashboardKpi {
  totalVentas: number;
  totalEfectivo: number;
  totalElectronico: number;
  totalRetirado: number;
  totalCortesias: number;
  totalPorConciliar: number;
  totalTickets: number;
  totalCajas: number;
  totalConciliaciones: number;
}

export interface VentaPorHora {
  hora: string;
  efectivo: number;
  electronico: number;
}

export interface VentaPorPuntoVenta {
  nombre: string;
  tipo: string;
  total: number;
  tickets: number;
  topProducto: string;
  topCantidad: number;
}

export interface TopProducto {
  nombre: string;
  cantidad: number;
  total: number;
}

export interface EstadoCaja {
  caja: string;
  inicio: number;
  venta_acumulada: number;
  monto_retirado: number;
  total_real: number;
  fecha?: string;
}

export interface ResumenConciliacion {
  folio: string;
  total_electronico: number;
  total_banco: number;
  total_punto_venta: number;
  total_materia_prima: number;
  total_a_pagar: number;
  split_banco: number;
  split_punto_venta: number;
}

export interface RemanenteKpi {
  totalTickets: number;
  totalCortesias: number;
  totalRecargas: number;
  totalRemanente: number;
}

export interface VentasPorCajaDetalle {
  puntoventa: string;
  brazaletes_efe: number;
  brazaletes_tar: number;
  ticketcash_efe: number;
  ticketcash_tar: number;
  brazaletes: number;
  ticketcash: number;
  total: number;
}

export interface VentasPorCajaKpi {
  total: number;
  detalle: VentasPorCajaDetalle[];
}

export interface VentasPorPuestoDetalle {
  puntoventa: string;
  producto: string;
  cantidad: number;
  total: number;
}

export interface VentasPorPuestosKpi {
  total: number;
  detalle: VentasPorPuestoDetalle[];
}

export interface CortesiaDetalle {
  folio: string;
  cliente: string;
  totalCortesia: number;
  totalConsumido: number;
  recarga: number;
  transferido: number;
  balance: number;
}

export interface CortesiasKpi {
  total: number;
  detalle: CortesiaDetalle[];
}

const EMPTY_REMANENTE_KPI: RemanenteKpi = {
  totalTickets: 0,
  totalCortesias: 0,
  totalRecargas: 0,
  totalRemanente: 0,
};

const EMPTY_VENTAS_POR_CAJA: VentasPorCajaKpi = { total: 0, detalle: [] };
const EMPTY_VENTAS_POR_PUESTOS: VentasPorPuestosKpi = { total: 0, detalle: [] };
const EMPTY_CORTESIAS: CortesiasKpi = { total: 0, detalle: [] };

const EMPTY_KPI: DashboardKpi = {
  totalVentas: 0, totalEfectivo: 0, totalElectronico: 0,
  totalRetirado: 0, totalCortesias: 0, totalPorConciliar: 0,
  totalTickets: 0, totalCajas: 0, totalConciliaciones: 0,
};

export function useDashboard(ids_dia: number[]) {
  const [loading, setLoading] = useState(false);
  const [kpi, setKpi] = useState<DashboardKpi>(EMPTY_KPI);
  const [montoLimite, setMontoLimite] = useState<number>(0);
  const [ventasPorHora, setVentasPorHora] = useState<VentaPorHora[]>([]);
  const [ventasPorPV, setVentasPorPV] = useState<VentaPorPuntoVenta[]>([]);
  const [topProductos, setTopProductos] = useState<TopProducto[]>([]);
  const [estadoCajas, setEstadoCajas] = useState<EstadoCaja[]>([]);
  const [conciliaciones, setConciliaciones] = useState<ResumenConciliacion[]>([]);
  const [remanenteKpi, setRemanenteKpi] = useState<RemanenteKpi>(EMPTY_REMANENTE_KPI);
  const [ventasPorCaja, setVentasPorCaja] = useState<VentasPorCajaKpi>(EMPTY_VENTAS_POR_CAJA);
  const [ventasPorPuestos, setVentasPorPuestos] = useState<VentasPorPuestosKpi>(EMPTY_VENTAS_POR_PUESTOS);
  const [cortesiasKpi, setCortesiasKpi] = useState<CortesiasKpi>(EMPTY_CORTESIAS);
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = useCallback(() => setRefreshTick(t => t + 1), []);

  useEffect(() => {
    if (ids_dia.length === 0) {
      setKpi(EMPTY_KPI);
      setVentasPorHora([]);
      setVentasPorPV([]);
      setTopProductos([]);
      setEstadoCajas([]);
      setConciliaciones([]);
      setRemanenteKpi(EMPTY_REMANENTE_KPI);
      setVentasPorCaja(EMPTY_VENTAS_POR_CAJA);
      setVentasPorPuestos(EMPTY_VENTAS_POR_PUESTOS);
      setCortesiasKpi(EMPTY_CORTESIAS);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        // Fetches globales + fetches por día en paralelo
        const [globalResults, perDayResults] = await Promise.all([
          Promise.allSettled([
            getReporteVentasCortesias(ids_dia),
            getMontoLimiteRetiro(),
            getReporteRemanente(ids_dia),
            getVentasPorCaja(ids_dia),
          ]),
          Promise.all(
            ids_dia.map(id => Promise.allSettled([
              getReporteVentas([id]),
              getReporteRetirosDashboard(id),
              getAllReportConciliations([id]),
            ]))
          ),
        ]);

        const [cortesiasResp, limiteResp, remanenteResp, ventasCajaResp] = globalResults;

        // Acumular datos de todos los días seleccionados
        const allDetalle: any[] = [];
        const allRetiros: any[] = [];
        const allConcil: any[]  = [];

        perDayResults.forEach(([ventasR, retirosR, concilR]) => {
          if (ventasR.status === 'fulfilled') {
            const ventasList: any[] = Array.isArray(ventasR.value) ? ventasR.value : [];
            allDetalle.push(...ventasList.flatMap((t: any) => t.detalle ?? []));
          }
          if (retirosR.status === 'fulfilled') {
            const retiros: any[] = Array.isArray(retirosR.value) ? retirosR.value : [];
            allRetiros.push(...retiros);
          }
          if (concilR.status === 'fulfilled') {
            const concilData = concilR.value;
            const concil: any[] = Array.isArray(concilData)
              ? concilData
              : (concilData?.rows ?? []);
            allConcil.push(...concil);
          }
        });

        const cortesiasRaw = cortesiasResp.status === 'fulfilled' ? cortesiasResp.value : [];
        const cortesiasList: any[] = Array.isArray(cortesiasRaw) ? cortesiasRaw : [];

        const limite = limiteResp.status === 'fulfilled' ? limiteResp.value : 0;
        setMontoLimite(limite);

        // ── Remanente KPI ─────────────────────────────────────────────────
        const remanenteRows: any[] = remanenteResp.status === 'fulfilled' && Array.isArray(remanenteResp.value)
          ? remanenteResp.value : [];
        setRemanenteKpi({
          totalTickets:   remanenteRows.length,
          totalCortesias: remanenteRows.reduce((s, r) => s + parseFloat(r.total_cortesia    ?? 0), 0),
          totalRecargas:  remanenteRows.reduce((s, r) => s + parseFloat(r.total_recarga_caja ?? 0), 0),
          totalRemanente: remanenteRows.reduce((s, r) => s + parseFloat(r.saldo_remanente   ?? 0), 0),
        });

        // ── KPI ──────────────────────────────────────────────────────────
        const totalVentas       = allDetalle.reduce((s, v) => s + parseFloat(v.total_venta ?? 0), 0);
        const totalEfectivo     = allDetalle.filter(v => v.forma_pago === 'Efectivo').reduce((s, v) => s + parseFloat(v.total_venta ?? 0), 0);
        const totalElectronico  = allDetalle.filter(v => v.forma_pago === 'Electrónico').reduce((s, v) => s + parseFloat(v.total_venta ?? 0), 0);
        const totalRetirado     = allRetiros.reduce((s, r) => s + parseFloat(r.monto_retirado ?? 0), 0);
        const totalCortesias    = cortesiasList.reduce((s, c) => s + parseFloat(c.resumen?.total_cortesia ?? 0), 0);
        const totalPorConciliar = allConcil.reduce((s, c) => s + parseFloat(c.total_a_pagar ?? 0), 0);
        const totalTickets      = new Set(allDetalle.map(v => v.folio_ticket).filter(Boolean)).size;
        const totalCajas        = new Set(allRetiros.map(r => r.caja)).size;

        setKpi({
          totalVentas, totalEfectivo, totalElectronico,
          totalRetirado, totalCortesias, totalPorConciliar,
          totalTickets, totalCajas, totalConciliaciones: allConcil.length,
        });

        // ── Ventas por Caja (Brazalete / TicketCash / Wallet) ────────────
        const ventasCajaRows: any[] = ventasCajaResp.status === 'fulfilled' && Array.isArray(ventasCajaResp.value)
          ? ventasCajaResp.value : [];
        const pvCajaMap: Record<string, { brazaletes_efe: number; brazaletes_tar: number; ticketcash_efe: number; ticketcash_tar: number }> = {};
        ventasCajaRows.forEach(r => {
          const pv = r.punto_venta ?? 'Sin nombre';
          if (!pvCajaMap[pv]) pvCajaMap[pv] = { brazaletes_efe: 0, brazaletes_tar: 0, ticketcash_efe: 0, ticketcash_tar: 0 };
          const amt = parseFloat(r.total_venta ?? 0);
          const esEfe = r.efectivo === true || r.efectivo === 1;
          const nombreProd = (r.producto ?? '').toLowerCase();
          if (nombreProd.includes('brazalete')) {
            if (esEfe) pvCajaMap[pv].brazaletes_efe += amt;
            else       pvCajaMap[pv].brazaletes_tar += amt;
          } else if (nombreProd.includes('ticketcash')) {
            if (esEfe) pvCajaMap[pv].ticketcash_efe += amt;
            else       pvCajaMap[pv].ticketcash_tar += amt;
          }
        });
        const cajasDetalle = Object.entries(pvCajaMap)
          .map(([puntoventa, d]) => ({
            puntoventa,
            brazaletes_efe: d.brazaletes_efe,
            brazaletes_tar: d.brazaletes_tar,
            ticketcash_efe: d.ticketcash_efe,
            ticketcash_tar: d.ticketcash_tar,
            brazaletes: d.brazaletes_efe + d.brazaletes_tar,
            ticketcash:  d.ticketcash_efe + d.ticketcash_tar,
            total: d.brazaletes_efe + d.brazaletes_tar + d.ticketcash_efe + d.ticketcash_tar,
          }))
          .sort((a, b) => b.total - a.total);
        setVentasPorCaja({
          total:   cajasDetalle.reduce((s, d) => s + d.total, 0),
          detalle: cajasDetalle,
        });

        // ── Ventas por hora ───────────────────────────────────────────────
        const horaMap: Record<string, { efectivo: number; electronico: number }> = {};
        allDetalle.forEach(v => {
          const h = moment(v.fecha_hora_venta).format('HH:00');
          if (!horaMap[h]) horaMap[h] = { efectivo: 0, electronico: 0 };
          if (v.forma_pago === 'Efectivo') horaMap[h].efectivo    += parseFloat(v.total_venta ?? 0);
          else                             horaMap[h].electronico += parseFloat(v.total_venta ?? 0);
        });
        setVentasPorHora(Object.keys(horaMap).sort().map(h => ({ hora: h, ...horaMap[h] })));

        // ── Ventas por Punto de Venta ─────────────────────────────────────
        const pvMap: Record<string, { total: number; tipo: string; tickets: Set<string>; prods: Record<string, number> }> = {};
        allDetalle.forEach(v => {
          const pv = v.punto_venta ?? 'Sin nombre';
          if (!pvMap[pv]) pvMap[pv] = { total: 0, tipo: v.tipo_punto_venta ?? '', tickets: new Set(), prods: {} };
          pvMap[pv].total += parseFloat(v.total_venta ?? 0);
          if (v.folio_ticket) pvMap[pv].tickets.add(v.folio_ticket);
          const prod = v.producto ?? 'Sin nombre';
          pvMap[pv].prods[prod] = (pvMap[pv].prods[prod] ?? 0) + parseInt(v.cantidad ?? 0);
        });
        setVentasPorPV(
          Object.entries(pvMap).map(([nombre, d]) => {
            const topEntry = Object.entries(d.prods).sort((a, b) => b[1] - a[1])[0];
            return { nombre, tipo: d.tipo, total: d.total, tickets: d.tickets.size, topProducto: topEntry?.[0] ?? '—', topCantidad: topEntry?.[1] ?? 0 };
          }).sort((a, b) => b.total - a.total)
        );

        // ── Top 10 Productos ──────────────────────────────────────────────
        const prodMap: Record<string, { cantidad: number; total: number }> = {};
        allDetalle.forEach(v => {
          const p = v.producto ?? 'Sin nombre';
          if (!prodMap[p]) prodMap[p] = { cantidad: 0, total: 0 };
          prodMap[p].cantidad += parseInt(v.cantidad ?? 0);
          prodMap[p].total    += parseFloat(v.total_venta ?? 0);
        });
        setTopProductos(
          Object.entries(prodMap)
            .map(([nombre, d]) => ({ nombre, ...d }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 10)
        );

        // ── Cortesías KPI ─────────────────────────────────────────────────────
        const cortesiasDetalle: CortesiaDetalle[] = cortesiasList
          .map((c: any) => ({
            folio:          c.folio ?? '—',
            cliente:        c.resumen?.cliente ?? '—',
            totalCortesia:  parseFloat(c.resumen?.total_cortesia        ?? 0),
            totalConsumido: parseFloat(c.resumen?.total_consumido_puesto ?? 0),
            recarga:        parseFloat(c.resumen?.saldo_recarga_caja     ?? 0),
            transferido:    parseFloat(c.resumen?.transferido            ?? 0),
            balance:        parseFloat(c.resumen?.balance               ?? 0),
          }))
          .sort((a: CortesiaDetalle, b: CortesiaDetalle) => b.totalCortesia - a.totalCortesia);
        setCortesiasKpi({
          total:   cortesiasDetalle.reduce((s, d) => s + d.totalCortesia, 0),
          detalle: cortesiasDetalle,
        });

        // ── Ventas por Puestos (agrupado por punto de venta + producto) ──────
        const pvProdMap: Record<string, { cantidad: number; total: number }> = {};
        allDetalle
          .filter(v => v.tipo_punto_venta !== 'CAJA')
          .forEach(v => {
            const key = `${v.punto_venta ?? 'Sin nombre'}|||${v.producto ?? 'Sin nombre'}`;
            if (!pvProdMap[key]) pvProdMap[key] = { cantidad: 0, total: 0 };
            pvProdMap[key].cantidad += parseInt(v.cantidad ?? 0);
            pvProdMap[key].total    += parseFloat(v.total_venta ?? 0);
          });
        const puestosDetalle = Object.entries(pvProdMap)
          .map(([key, d]) => {
            const [puntoventa, producto] = key.split('|||');
            return { puntoventa, producto, cantidad: d.cantidad, total: d.total };
          })
          .sort((a, b) => b.total - a.total);
        setVentasPorPuestos({
          total:   puestosDetalle.reduce((s, d) => s + d.total, 0),
          detalle: puestosDetalle,
        });

        // ── Estado de Cajas (acumulado por nombre de caja) ────────────────
        const cajaMap: Record<string, EstadoCaja> = {};
        allRetiros.forEach(r => {
          const key = r.caja ?? 'Sin nombre';
          if (!cajaMap[key]) {
            cajaMap[key] = { caja: key, inicio: 0, venta_acumulada: 0, monto_retirado: 0, total_real: 0, fecha: r.fecha ?? r.fecha_ingreso };
          }
          cajaMap[key].inicio          += parseFloat(r.inicio ?? 0);
          cajaMap[key].venta_acumulada += parseFloat(r.venta_acumulada ?? 0);
          cajaMap[key].monto_retirado  += parseFloat(r.monto_retirado ?? 0);
          cajaMap[key].total_real      += parseFloat(r.total_real ?? 0);
        });
        setEstadoCajas(Object.values(cajaMap));

        // ── Conciliaciones ────────────────────────────────────────────────
        setConciliaciones(
          allConcil.map((c, i) => ({
            folio:               c.folio ?? `Conciliación #${i + 1}`,
            total_electronico:   parseFloat(c.total_saldo_electronico ?? 0),
            total_banco:         parseFloat(c.total_banco ?? 0),
            total_punto_venta:   parseFloat(c.total_punto_venta ?? 0),
            total_materia_prima: parseFloat(c.total_materia_prima ?? 0),
            total_a_pagar:       parseFloat(c.total_a_pagar ?? 0),
            split_banco:         c.split_banco ?? 0,
            split_punto_venta:   c.split_punto_venta ?? 0,
          }))
        );

      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [ids_dia.join(','), refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  return { loading, kpi, remanenteKpi, montoLimite, ventasPorHora, ventasPorPV, topProductos, estadoCajas, conciliaciones, ventasPorCaja, ventasPorPuestos, cortesiasKpi, refresh };
}
